/* maxlock-core.js — <maxlock-core> (sections/maxlock-core.liquid).

   Replaces the source's GSAP ScrollTrigger timeline with one number. With the
   effect on and motion allowed the element gets [data-animated], which turns
   on the CSS pin (sticky under the header + a spacer), and this script writes
   the scroll progress through that spacer to --maxlock-p. The stylesheet turns
   that into every frame: the background fade, the staggered card rise and the
   bullets.

   Smoothing stands in for ScrollTrigger's `scrub: <seconds>`: the published
   value chases the scroll position and gets ~95% of the way there in the
   configured time, so a flick of the wheel plays out as a glide. 0 follows the
   scroll exactly. Reduced motion is followed live: switching it on drops the
   pin and leaves the last frame. */

const desktop = window.matchMedia('(min-width: 750px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class MaxlockCore extends HTMLElement {
  connectedCallback() {
    this.sticky = this.querySelector('.fs-maxlock__sticky');
    this.space = this.querySelector('.fs-maxlock__track-space');
    if (!this.sticky || !this.space) return;

    this.onScroll = () => {
      this.target = this.progress();
      if (this.frame) return;
      this.last = performance.now();
      this.frame = window.requestAnimationFrame(this.tick);
    };
    this.tick = (now) => {
      const seconds = parseFloat(desktop.matches ? this.dataset.scrub : this.dataset.scrubMobile) || 0;
      const elapsed = Math.min(0.1, (now - this.last) / 1000);
      this.last = now;
      if (seconds > 0) {
        this.current += (this.target - this.current) * (1 - Math.exp((-3 * elapsed) / seconds));
      }
      if (seconds <= 0 || Math.abs(this.target - this.current) < 0.0005) this.current = this.target;
      this.write(this.current);
      this.frame = this.current === this.target ? 0 : window.requestAnimationFrame(this.tick);
    };
    this.onModeChange = () => this.syncMode();
    reducedMotion.addEventListener('change', this.onModeChange);

    /* The pin keeps a section that is taller than the screen under the
       header bottom-aligned (see the stylesheet), and CSS cannot read the
       section's own height — publish it. */
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.style.setProperty('--maxlock-h', `${this.sticky.offsetHeight}px`);
      });
      this.resizeObserver.observe(this.sticky);
    }

    this.syncMode();
  }

  disconnectedCallback() {
    if (!this.onModeChange) return;
    reducedMotion.removeEventListener('change', this.onModeChange);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.listen(false);
  }

  syncMode() {
    const animate = this.hasAttribute('data-animate') && !reducedMotion.matches;
    this.toggleAttribute('data-animated', animate);
    this.listen(animate);
    if (!animate) {
      this.style.removeProperty('--maxlock-p');
      return;
    }
    /* Start where the page already is (a reload halfway down, a back
       navigation) instead of gliding in from the first frame. */
    this.current = this.target = this.progress();
    this.write(this.current);
  }

  listen(on) {
    if (on === Boolean(this.listening)) return;
    this.listening = on;
    if (on) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      return;
    }
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  /* 0 when the section's top meets the pin line, 1 once the spacer has been
     scrolled through. The pin line is the used sticky offset (the header). */
  progress() {
    const distance = this.space.offsetHeight;
    if (!distance) return 1;
    const pinLine = parseFloat(getComputedStyle(this.sticky).top) || 0;
    const scrolled = pinLine - this.getBoundingClientRect().top;
    return Math.min(1, Math.max(0, scrolled / distance));
  }

  write(value) {
    this.style.setProperty('--maxlock-p', value.toFixed(4));
  }
}

if (!customElements.get('maxlock-core')) customElements.define('maxlock-core', MaxlockCore);
